import { defineComponent as _defineComponent } from 'vue'
import { ICountry } from "@/api/types/catalogue";
import {
    assignManager,
    changeAffiliateStatus,
    createOrAddMasterAccountV3,
    getActiveUserSearch,
    getAffiliateSearch,
    getUsersMasterAccountV3,
    verifiedAffiliateEmail,
} from "@/api/user";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import BaseList from "@/components-UI/list/BaseList.vue";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import { messengerItems } from "@/mappings/messengers";
import { Affiliate } from "@/services/affiliates/Affiliate";
import store from "@/store";
import { IBaseAutocomplete } from "@/types";
import { showNotification, showServerError } from "@/utils";
import { getFormatDate } from "@/utils/formatDates";
import { translatePartnerStatus } from "@/utils/translate";
import { isSlaveAffiliate, MANAGER_SLAVE_HINT, withManagerRejectionMessage } from "@/utils/managerAssignment";
import { computed, inject } from "vue";
import { useRouter } from "vue-router/composables";
import AffiliateMainInfoItem from "./AffiliateMainInfoItem.vue";
import { AFFILIATE_EDIT_KEY, AFFILIATE_INFO_EDIT_TYPE, AFFILIATE_INFO_TYPE, EDITABLE_FIELD } from "./types";
import { useCardEdit } from "./useCardEdit";
import { VALIDATION_RULES } from "./useEditableFields";

interface Props {
    affiliate: Affiliate;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AboutCard',
  props: {
    affiliate: null
  },
  emits: ["saved"],
  setup(__props: any, { emit }: { emit: ({ (e: 'saved'): void }), expose: any, slots: any, attrs: any }) {

const props = __props as {
    affiliate: Affiliate;
}



const router = useRouter();
const ctx = inject(AFFILIATE_EDIT_KEY)!;

const OWNED_KEYS: EDITABLE_FIELD[] = [
    EDITABLE_FIELD.MASTER_ID,
    EDITABLE_FIELD.STATUS,
    EDITABLE_FIELD.MANAGER,
    EDITABLE_FIELD.EMAIL,
    EDITABLE_FIELD.PHONE,
    EDITABLE_FIELD.LAST_NAME,
    EDITABLE_FIELD.FIRST_NAME,
    EDITABLE_FIELD.NICKNAME,
    EDITABLE_FIELD.MESSENGER,
    EDITABLE_FIELD.NEWCOMER,
    EDITABLE_FIELD.BIRTHDAY,
    EDITABLE_FIELD.COUNTRY,
    EDITABLE_FIELD.CITY,
];

// === Update methods ===

const updateMasterAccount = async () => {
    const masterId = ctx.editableFields.value[EDITABLE_FIELD.MASTER_ID];
    if (!masterId || masterId === props.affiliate.master_id) return;

    const { data } = await getUsersMasterAccountV3({ account_id: masterId });
    try {
        // Привязка к мастеру переводит партнёра на менеджера мастер-группы, поэтому бекенд
        // отклоняет мастера без менеджера - сообщение надо показать человеческим текстом.
        await createOrAddMasterAccountV3({
            account_id: data.master_id !== null ? data.master_id : masterId,
            account_ids: [props.affiliate.id!],
        });
    } catch (err) {
        throw withManagerRejectionMessage(err);
    }
};

const updateStatus = async () => {
    await changeAffiliateStatus(props.affiliate.id!, {
        status: ctx.editableFields.value[EDITABLE_FIELD.STATUS],
    });
};

const updateManager = async () => {
    try {
        await assignManager(props.affiliate.id!, {
            manager_id: ctx.editableFields.value[EDITABLE_FIELD.MANAGER],
        });
    } catch (err) {
        throw withManagerRejectionMessage(err);
    }
};

// === Email verification ===

const isAlreadyVerifyEmail = computed(() => {
    return !!store.getters["GET_AFFILIATE_VERIFIED_EMAILS"](props.affiliate.id!) || props.affiliate.email_verified;
});

const isHideConfirmButton = computed(() => {
    const isNotApprovedAndChanged =
        !props.affiliate.email_verified && props.affiliate.email !== ctx.editableFields.value[EDITABLE_FIELD.EMAIL];
    return isAlreadyVerifyEmail.value || isNotApprovedAndChanged;
});

const verifiedEmail = async () => {
    try {
        await verifiedAffiliateEmail({ affiliate_ids: [props.affiliate.id!], verified: true });
        showNotification("Партнёру отправлено письмо со ссылкой для подтверждения почты");
        store.commit("SET_AFFILIATE_VERIFIED_EMAILS", props.affiliate.id!);
    } catch (e) {
        if (e.status === 404 && e.data?.error?.message?.includes("has an active link")) {
            showNotification("Партнёру уже отправлено письмо со ссылкой для подтверждения почты.");
            store.commit("SET_AFFILIATE_VERIFIED_EMAILS", props.affiliate.id!);
        } else {
            showServerError(e, "Ошибка верификации почты");
            resetEmailVerification();
        }
    }
};

const resetEmailVerification = () => {
    if (isAlreadyVerifyEmail.value && props.affiliate.email !== ctx.editableFields.value[EDITABLE_FIELD.EMAIL]) {
        store.commit("DELETE_AFFILIATE_VERIFIED_EMAILS", props.affiliate);
    }
};

// === Computed helpers ===

const getAffiliateLink = computed(
    () =>
        router.resolve({
            name: "affiliate",
            params: { id: String(props.affiliate.id!) },
        }).href,
);

const getRegistrationLink = computed(
    () =>
        router.resolve({
            name: "registration",
            params: { id: String(props.affiliate.registration_id!) },
        }).href,
);

const getMasterValue = computed(() => props.affiliate.master_id);

const isMaster = computed(() => {
    return !!props.affiliate.has_slaves && props.affiliate.master_id === props.affiliate.id;
});

const isEditableMaster = computed(() => {
    return props.affiliate.has_slaves === 0 && props.affiliate.master_id === props.affiliate.id;
});

const isSlave = computed(() => isSlaveAffiliate(props.affiliate));

const getMasterLink = computed(
    () =>
        router.resolve({
            name: "affiliate",
            params: { id: String(props.affiliate.master_id!) },
        }).href,
);

const getMasterAutocompleteData = computed(
    (): IBaseAutocomplete => ({
        apiMethod: getAffiliateSearch,
        template: (i) => `${i.id} ${i.first_name} ${i.last_name}`,
        itemValue: "id",
    }),
);

const getStatusItems = computed((): { id: string; text: string }[] => {
    const statuses = ["active", "pending", "deleted", "blocked", "rejected"];
    return statuses.map((i) => ({ id: i, text: translatePartnerStatus(i).text }));
});

const getManagerSelectData = computed(
    (): IBaseAutocomplete => ({
        apiMethod: getActiveUserSearch,
        template: (i) => `${i.first_name} ${i.last_name} ${i.id}`,
        itemValue: "id",
        multiple: false,
        multiPaste: false,
        disabled: isSlave.value,
    }),
);

const getMessengerData = computed(() => {
    const mes = props.affiliate.messenger ?? null;
    const icon = messengerItems.find((i) => i.type === mes?.type);
    return { icon: icon?.logo, text: mes?.account };
});

const isDisabledNewcomer = computed(() => !props.affiliate.newcomer);

const getDateTextValue = computed(() => {
    return (field: string, supField?: string) => {
        const isWithoutTime = field === "birthday";
        const value = supField ? props.affiliate[supField][field] : props.affiliate[field];
        if (!value) return "";
        return `${getFormatDate(value, "d MMMM yyyy")} <span>${isWithoutTime ? "" : getFormatDate(value, "HH:mm")}</span>`;
    };
});

const getCountryItems = computed((): ICountry[] => {
    return store.getters["catalogueModule/GET_COUNTRIES"];
});

const getCountryTranslate = computed(() => {
    const code = props.affiliate.country;
    const country = getCountryItems.value.find((i) => i.code === code);
    return country ? country.name : code;
});

// === Card edit ===

const { isEdit, isLoading, isDisableEdit, editInfo, resetInfo } = useCardEdit({
    affiliateId: () => props.affiliate.id!,
    getItems: () => flatItems.value,
    editableFields: () => ctx.editableFields.value,
    getChangedValues: () => ctx.getChangedValues.value,
    resetFields: ctx.resetFields,
    ownedKeys: OWNED_KEYS,
    onAfterSave: () => {
        resetEmailVerification();
        emit("saved");
    },
});

// === Items ===

const aboutItems = computed(() => [
    {
        name: "О ПАРТНЁРЕ",
        items: {
            id: {
                type: AFFILIATE_INFO_TYPE.CHIP,
                name: "ID аккаунта",
                value: props.affiliate.id,
                link: getAffiliateLink.value,
            },
            master_id: {
                type: AFFILIATE_INFO_TYPE.CHIP,
                name: "ID мастера",
                value: getMasterValue.value,
                link: getMasterLink.value,
                isMaster: isMaster.value,
                isEditable: isEditableMaster.value,
                editType: AFFILIATE_INFO_EDIT_TYPE.AUTOCOMPLETE,
                autocompleteData: getMasterAutocompleteData.value,
                editMethod: ctx.editItem,
                updateMethod: updateMasterAccount,
            },
            registration_id: {
                type: AFFILIATE_INFO_TYPE.CHIP,
                name: "ID регистрации",
                value: props.affiliate.registration_id,
                link: getRegistrationLink.value,
            },
            status: {
                type: AFFILIATE_INFO_TYPE.STATUS,
                name: "Статус",
                value: ctx.editableFields.value[EDITABLE_FIELD.STATUS],
                isEditable: true,
                editType: AFFILIATE_INFO_EDIT_TYPE.SELECT,
                translateMethod: translatePartnerStatus,
                selectItems: getStatusItems.value,
                editMethod: ctx.editItem,
                updateMethod: updateStatus,
            },
            manager: {
                type: AFFILIATE_INFO_TYPE.TEXT,
                name: "Менеджер",
                value: ctx.editableFields.value[EDITABLE_FIELD.MANAGER],
                defaultValue: props.affiliate.manager?.id,
                isEditable: true,
                editType: AFFILIATE_INFO_EDIT_TYPE.AUTOCOMPLETE,
                autocompleteData: getManagerSelectData.value,
                textValue: `${props.affiliate.manager?.first_name} ${props.affiliate.manager?.last_name} ${props.affiliate.manager?.id}`,
                editMethod: ctx.editItem,
                updateMethod: updateManager,
                disabled: props.affiliate.manager?.manager_status,
                disabledHint: isSlave.value ? MANAGER_SLAVE_HINT : '',
            },
            email: {
                type: !isEdit.value ? AFFILIATE_INFO_TYPE.VERIFIED : AFFILIATE_INFO_TYPE.EMAIL,
                name: "E-mail",
                value: ctx.editableFields.value[EDITABLE_FIELD.EMAIL],
                isValid: ctx.isFieldValid(VALIDATION_RULES.EMAIL, EDITABLE_FIELD.EMAIL).value,
                isVerified: props.affiliate.email_verified,
                isHideConfirmButton: isHideConfirmButton.value,
                isDisabledBtn: isAlreadyVerifyEmail.value || ctx.editableFields.value[EDITABLE_FIELD.EMAIL],
                isEditable: true,
                editType: AFFILIATE_INFO_EDIT_TYPE.EMAIL,
                editMethod: ctx.editItem,
                checkEmail: verifiedEmail,
                clear: () => {
                    store.commit("DELETE_AFFILIATE_VERIFIED_EMAILS", props.affiliate);
                },
            },
            phone: {
                type: AFFILIATE_INFO_TYPE.VERIFIED,
                name: "Телефон",
                value: ctx.editableFields.value[EDITABLE_FIELD.PHONE],
                isVerified: props.affiliate.phone_verified,
                isEditable: true,
                editType: AFFILIATE_INFO_EDIT_TYPE.PHONE,
                editMethod: ctx.editItem,
            },
        },
    },
    {
        name: "ПРОФИЛЬ",
        items: {
            last_name: {
                type: AFFILIATE_INFO_TYPE.TEXT,
                name: "Фамилия",
                value: ctx.editableFields.value[EDITABLE_FIELD.LAST_NAME],
                isValid: ctx.isFieldValid(VALIDATION_RULES.TEXT, EDITABLE_FIELD.LAST_NAME).value,
                isEditable: true,
                editType: AFFILIATE_INFO_EDIT_TYPE.TEXT,
                editMethod: ctx.editItem,
            },
            first_name: {
                type: AFFILIATE_INFO_TYPE.TEXT,
                name: "Имя",
                value: ctx.editableFields.value[EDITABLE_FIELD.FIRST_NAME],
                isValid: ctx.isFieldValid(VALIDATION_RULES.TEXT, EDITABLE_FIELD.FIRST_NAME).value,
                isEditable: true,
                editType: AFFILIATE_INFO_EDIT_TYPE.TEXT,
                editMethod: ctx.editItem,
            },
            nickname: {
                type: AFFILIATE_INFO_TYPE.TEXT,
                name: "Никнейм",
                value: ctx.editableFields.value[EDITABLE_FIELD.NICKNAME],
                isEditable: true,
                editType: AFFILIATE_INFO_EDIT_TYPE.TEXT,
                editMethod: ctx.editItem,
            },
            messenger: {
                type: AFFILIATE_INFO_TYPE.MESSENGER,
                name: "Мессенджер",
                value: ctx.editableFields.value[EDITABLE_FIELD.MESSENGER],
                isValid: ctx.isMessengerValid.value,
                isEditable: true,
                editType: AFFILIATE_INFO_EDIT_TYPE.MESSENGER,
                textValue: getMessengerData.value.text,
                icon: getMessengerData.value.icon,
                editMethod: ctx.editItem,
            },
            newcomer: {
                type: AFFILIATE_INFO_TYPE.SWITCH,
                name: "Новорег",
                value: ctx.editableFields.value[EDITABLE_FIELD.NEWCOMER],
                trueValue: true,
                falseValue: false,
                isEditable: true,
                isDisabled: isDisabledNewcomer.value && isEdit.value,
                editMethod: ctx.editItem,
            },
            learned_from: {
                type: AFFILIATE_INFO_TYPE.TEXT,
                name: "Откуда узнал",
                value: props.affiliate.learned_from,
            },
        },
    },
    {
        name: "ЛИЧНЫЕ ДАННЫЕ",
        items: {
            birthday: {
                type: AFFILIATE_INFO_TYPE.DATE,
                name: "Дата рождения",
                value: ctx.editableFields.value[EDITABLE_FIELD.BIRTHDAY],
                textValue: getDateTextValue.value("birthday"),
                isEditable: true,
                editType: AFFILIATE_INFO_EDIT_TYPE.DATE,
                editMethod: ctx.editItem,
            },
            country: {
                type: AFFILIATE_INFO_TYPE.TEXT,
                name: "Страна проживания",
                value: ctx.editableFields.value[EDITABLE_FIELD.COUNTRY],
                isEditable: true,
                textValue: getCountryTranslate.value,
                editType: AFFILIATE_INFO_EDIT_TYPE.SELECT,
                selectItems: getCountryItems.value,
                selectData: {
                    itemText: "name",
                    itemValue: "code",
                    isMultiple: false,
                    isAutocomplete: true,
                },
                editMethod: ctx.editItem,
            },
            city: {
                type: AFFILIATE_INFO_TYPE.TEXT,
                name: "Город проживания",
                value: ctx.editableFields.value[EDITABLE_FIELD.CITY],
                isEditable: true,
                editType: AFFILIATE_INFO_EDIT_TYPE.TEXT,
                editMethod: ctx.editItem,
            },
        },
    },
    {
        name: "ДАТЫ",
        items: {
            signup_at: {
                type: AFFILIATE_INFO_TYPE.DATE,
                name: "Зарегистрирован",
                value: props.affiliate.signup_at,
                textValue: getDateTextValue.value("signup_at"),
            },
            created_at: {
                type: AFFILIATE_INFO_TYPE.DATE,
                name: "Создан",
                value: props.affiliate.created_at,
                textValue: getDateTextValue.value("created_at"),
            },
            updated_at: {
                type: AFFILIATE_INFO_TYPE.DATE,
                name: "Обновлён",
                value: props.affiliate.updated_at,
                textValue: getDateTextValue.value("updated_at"),
            },
            last_login: {
                type: AFFILIATE_INFO_TYPE.DATE,
                name: "Последний логин",
                value: props.affiliate.last_login?.created_at,
                textValue: getDateTextValue.value("created_at", "last_login"),
            },
        },
    },
]);

const flatItems = computed(() =>
    aboutItems.value.reduce((acc, block) => ({ ...acc, ...block.items }), {} as Record<string, any>),
);

return { __sfc: true,props, emit, router, ctx, OWNED_KEYS, updateMasterAccount, updateStatus, updateManager, isAlreadyVerifyEmail, isHideConfirmButton, verifiedEmail, resetEmailVerification, getAffiliateLink, getRegistrationLink, getMasterValue, isMaster, isEditableMaster, isSlave, getMasterLink, getMasterAutocompleteData, getStatusItems, getManagerSelectData, getMessengerData, isDisabledNewcomer, getDateTextValue, getCountryItems, getCountryTranslate, isEdit, isLoading, isDisableEdit, editInfo, resetInfo, aboutItems, flatItems, BaseEditableCard, BaseList, BaseListItem, AffiliateMainInfoItem }
}

})